import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBoundStore } from "../../../Store/store";
import { BaseCard } from "../../../Components/Cards/BaseCard";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { DateTimePickerInput } from "../../../Components/Form/DateTimePickerInput";
import { NumberInput } from "../../../Components/Form/NumberInput";
import { TextAreaInput } from "../../../Components/Form/TextAreaInput";
import { ControlledSwitch } from "../../../Components/Form/ControlledSwitch";
import { PrimaryButton } from "../../../Components/Buttons/PrimaryButton";
import { useTranslation } from "react-i18next";
import { DangerButton } from "../../../Components/Buttons/DangerButton";
import { TextInputReadOnly } from "../../../Components/Form/TextInputReadOnly";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faDownload, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { DeleteConfirmDialog } from "../../../Components/Dialogs/Confirmation/DeleteConfirmDialog";
import { SelectInput } from "../../../Components/Form/SelectInput";
import toast from "react-hot-toast";
import { MailHistoryList } from "../../../Components/MailHistory/MailHistoryList";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";

const InterruptedShiftToggleRow = ({ control, getValues, setValue, errors, showAsManual, onReset, t }) => {
    return (
        <div className="mb-2">
            <div className="flex flex-row justify-start items-center gap-2">
                <ControlledSwitch
                    control={control}
                    name={'interrupted_shift'}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    translationIndex={'shifts.interrupted_shift'}
                />
                <span
                    className={
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ' +
                        (showAsManual ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700')
                    }
                    title={
                        showAsManual
                            ? 'Deze status is handmatig door een admin gezet.'
                            : 'Deze status is automatisch bepaald op basis van de werkblok-regels.'
                    }
                >
                    {showAsManual
                        ? t('shifts.interrupted_shift_source_manual')
                        : t('shifts.interrupted_shift_source_auto')}
                </span>
                {showAsManual && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="text-gray-500 hover:text-gray-800 text-xs"
                        title="Terug naar automatisch bepaalde waarde"
                    >
                        <FontAwesomeIcon icon={faRotateLeft} />
                    </button>
                )}
            </div>
        </div>
    );
};

export const AdminShiftDetails = () => {
    const {
        shiftId
    } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const getShiftDetail = useBoundStore((state) => state.getShiftDetail);
    const shiftDetail = useBoundStore((state) => state.shiftDetail);
    const deleteShift = useBoundStore((state) => state.deleteShift);
    const updateShift = useBoundStore((state) => state.updateShift);
    const getAllFreelancers = useBoundStore((state) => state.getAllFreelancers);
    const freelancers = useBoundStore((state) => state.freelancers);
    const withdrawShift = useBoundStore((state) => state.withdrawShift);

    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const [showWithdrawConfirmation, setShowWithdrawConfirmation] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [resetIntent, setResetIntent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const suppressResetClearRef = useRef(false);

    const shiftApplicationPossibilities = [
        { id: 1, value: 'both', label: t('shifts.both') },
        { id: 2, value: 'only_verpleegkundige', label: t('shifts.filter_only_nurse') },
        { id: 3, value: 'only_zorgkundige', label: t('shifts.filter_only_caregiver') },
    ]

    const statutenOptions = [
        { id: 1, value: 'freelance', label: t('statuten.freelance') },
        { id: 2, value: 'interim', label: t('statuten.interim') },
        { id: 3, value: 'student', label: t('statuten.student') },
        { id: 4, value: 'flexi_job', label: t('statuten.flexi_job') },
    ]

    const onDeleteShift = () => {
        deleteShift(shiftId).then(() => {
            navigate('/admin/shifts');
            toast.success(t('shifts.toast_deleted'));
            setOpenDeleteConfirmation(false);
        });
    }

    const handleWithdrawFreelancer = async () => {
        if (!shiftDetail?.id) {
            return;
        }

        setIsWithdrawing(true);
        try {
            const success = await withdrawShift(shiftDetail.id);
            if (success) {
                setShowWithdrawConfirmation(false);
                const updatedDetail = await getShiftDetail(shiftId);
                if (updatedDetail) {
                    reset({
                        start_time: dayjs(updatedDetail.start_time).format('YYYY-MM-DD HH:mm:ss'),
                        end_time: dayjs(updatedDetail.end_time).format('YYYY-MM-DD HH:mm:ss'),
                        break_duration: updatedDetail.break_duration ?? null,
                        is_published: updatedDetail.is_published ?? false,
                        remarks: updatedDetail.remarks ?? null,
                        shift_applications_possibility: updatedDetail.shift_applications_possibility ?? 'both',
                        allowed_statuten: updatedDetail.allowed_statuten?.length ? updatedDetail.allowed_statuten : ['freelance'],
                        freelancer_id: updatedDetail.freelancer?.id ?? null,
                        branch_id: updatedDetail.branch?.id ?? null,
                    });
                }
            }
        } finally {
            setIsWithdrawing(false);
        }
    };

    useEffect(() => {
        const statuten = shiftDetail?.allowed_statuten?.length ? shiftDetail.allowed_statuten : [];
        getAllFreelancers(null, null, true, statuten);
    }, [getAllFreelancers, shiftDetail?.allowed_statuten]);

    const schema = yup.object({
        start_time: yup.string().required(),
        end_time: yup.string().required(),
        break_duration: yup.string().nullable(),
        published: yup.boolean(),
        remarks: yup.string().nullable(),
        shift_repeats_enabled: yup.boolean().nullable(),
        shift_repeats: yup.array().nullable(),
        allowed_statuten: yup.array().of(yup.string()).min(1, t('shifts_module.allowed_statuten_min')).required(),
        freelancer_id: yup.string().nullable(),
        applied_as_statuut: yup.string().nullable(),
        interrupted_shift: yup.boolean()
    }).required()

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        setValue,
        control,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues: {
            branch_id: null,
            start_time: null,
            end_time: null,
            break_duration: null,
            is_published: false,
            remarks: null,
            shift_repeats_enabled: false,
            shift_repeats: [],
            freelancer_id: null,
            applied_as_statuut: null,
            interrupted_shift: false,
        }
    });

    const watchedFreelancerId = useWatch({ control, name: 'freelancer_id' });
    const watchedAllowedStatuten = useWatch({ control, name: 'allowed_statuten' });
    const watchedPossibility = useWatch({ control, name: 'shift_applications_possibility' });

    const matchesPossibility = (freelancer, possibility) => {
        if (!possibility || possibility === 'both') return true;
        const fn = freelancer?.function;
        if (possibility === 'only_verpleegkundige') return ['A1', 'A2'].includes(fn);
        if (possibility === 'only_zorgkundige') return ['A0', 'A3'].includes(fn);
        return true;
    };

    useEffect(() => {
        if (!watchedFreelancerId) return;
        const selected = freelancers.find(f => String(f.id) === String(watchedFreelancerId));
        if (selected && !matchesPossibility(selected, watchedPossibility)) {
            setValue('freelancer_id', null);
        }
    }, [watchedPossibility, watchedFreelancerId, freelancers, setValue]);

    const matchingStatutenOptions = React.useMemo(() => {
        if (!watchedFreelancerId || !watchedAllowedStatuten?.length) return [];
        const selectedFreelancer = freelancers.find(f => String(f.id) === String(watchedFreelancerId));
        if (!selectedFreelancer) return [];
        const freelancerStatuten = [
            selectedFreelancer.main_statuut,
            ...(selectedFreelancer.extra_statuten || [])
        ].filter(Boolean);
        return watchedAllowedStatuten
            .filter(s => freelancerStatuten.includes(s))
            .map(s => ({
                id: s,
                value: s,
                label: t(`statuten.${s}`),
            }));
    }, [watchedFreelancerId, watchedAllowedStatuten, freelancers, t]);

    const previousFreelancerIdRef = React.useRef(undefined);
    useEffect(() => {
        const prev = previousFreelancerIdRef.current;
        previousFreelancerIdRef.current = watchedFreelancerId;
        if (!prev) return;
        if (prev === watchedFreelancerId) return;
        setValue('applied_as_statuut', null);
    }, [watchedFreelancerId, setValue]);

    const watchedInterruptedShift = useWatch({ control, name: 'interrupted_shift' });
    useEffect(() => {
        if (suppressResetClearRef.current) {
            suppressResetClearRef.current = false;
            return;
        }
        if (resetIntent) {
            setResetIntent(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedInterruptedShift]);

    useEffect(() => {
        if (!shiftId) return;

        getShiftDetail(shiftId).then((detail) => {
            if (!detail) {
                console.warn('No shift detail returned for ID:', shiftId);
                return;
            }

            if (!detail.start_time || !detail.end_time) {
                console.warn('Shift detail missing required fields:', detail);
                return;
            }

            reset({
                start_time: dayjs(detail.start_time).format('YYYY-MM-DD HH:mm:ss'),
                end_time: dayjs(detail.end_time).format('YYYY-MM-DD HH:mm:ss'),
                break_duration: detail.break_duration ?? null,
                is_published: detail.is_published ?? false,
                remarks: detail.remarks ?? null,
                shift_applications_possibility: detail.shift_applications_possibility ?? 'both',
                allowed_statuten: detail.allowed_statuten?.length ? detail.allowed_statuten : ['freelance'],
                freelancer_id: detail.freelancer?.id ?? null,
                applied_as_statuut: detail.confirmed_applied_as_statuut ?? null,
                branch_id: detail.branch?.id ?? null,
                interrupted_shift: detail.interrupted_shift ?? false,
            });
        }).catch(error => {
            console.error('Error loading shift detail:', error);
        });
    }, [getShiftDetail, shiftId, reset]);

    const onSubmit = (data) => {
        if (isSubmitting) return;
        if (matchingStatutenOptions.length > 1 && !data.applied_as_statuut) {
            toast.error(t('shifts_module.applied_as_statuut_required'));
            return;
        }

        const transformedData = {
            ...data,
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
            is_published: data.is_published === "" ? false : data.is_published,
            shift_applications_possibility: data?.shift_applications_possibility ?? 'both',
            freelancer_id: data.freelancer_id ?? null,
            applied_as_statuut: data.applied_as_statuut ?? null,
            branch_id: data.branch_id ?? null,
        }

        const interruptedChanged =
            (data.interrupted_shift ?? false) !== (shiftDetail?.interrupted_shift ?? false);

        if (resetIntent) {
            transformedData.interrupted_shift = null;
        } else if (interruptedChanged) {
            transformedData.interrupted_shift = data.interrupted_shift ?? false;
        } else {
            delete transformedData.interrupted_shift;
        }

        setIsSubmitting(true);
        updateShift(shiftId, transformedData).then(async () => {
            setResetIntent(false);
            const updated = await getShiftDetail(shiftId);
            if (updated) {
                setValue('interrupted_shift', updated.interrupted_shift ?? false);
            }
            toast.success(t('shifts.toast_saved'));
        }).catch((err) => {
            console.error('Update shift failed', {
                shiftId,
                status: err?.response?.status,
                data: err?.response?.data,
            });
        }).finally(() => {
            setIsSubmitting(false);
        });
    }

    const handleResetInterrupted = () => {
        suppressResetClearRef.current = true;
        setValue(
            'interrupted_shift',
            shiftDetail?.interrupted_shift_auto_value ?? false,
            { shouldDirty: true }
        );
        setResetIntent(true);
    };

    const parseAddressField = (user, item, idx) => {
        return (
            <div className="mb-2 flex flex-col justify-start" key={idx}>
                <p className="font-bold leading-3 -mb-2 text-lg mt-4">{t(`general.fields.address`)}:</p>
                <div>{Object.entries(user[item] ?? {})?.map(([key, value], idx) => {
                    return (
                        <TextInputReadOnly translationIndex={key} value={value ?? '-'} key={idx} />
                    )
                })}</div>
            </div>
        )
    }

    const parseBranchesField = (user, item, idx, indexForced = 0) => {
        const forcedBranch = user[item][indexForced] ?? user[item];

        if (Array.isArray(forcedBranch)) {
            return (
                <div className="mb-2 flex flex-col justify-start" key={idx}>
                    <p className="font-bold leading-3 -mb-2 text-lg mt-4">{t(`general.fields.address`)}:</p>
                    <div>{forcedBranch?.map((branch, idx) => {
                        return (
                            <TextInputReadOnly translationIndex={`general.fields.${branch}`} value={branch ?? '-'} key={idx} />
                        )
                    })}</div>
                </div>
            )
        } else {
            return <div className="mb-2 flex flex-col justify-start" key={idx}>
                <p className="font-bold leading-3 -mb-2 text-lg mt-4">{t(`general.fields.address`)}:</p>
                <div>{Object.entries(forcedBranch ?? {})?.map(([key, value], idx) => {
                    if ([
                        'id',
                        'max_hourly_rate_vk',
                        'max_hourly_rate_zk',
                        'shift_confirmed_message',
                        'alias',
                        'invoice_email',
                        'invoice_company_name',
                        'invoice_vat_number',
                        'invoice_street',
                        'invoice_number',
                        'invoice_bus',
                        'invoice_city',
                        'invoice_postal',
                        'contracts_email',
                        'contracts_firstname',
                        'contracts_lastname',
                        'performancestatement_email',
                        'performancestatement_firstname',
                        'performancestatement_lastname',
                    ].includes(key)) return false;
                    return (
                        <TextInputReadOnly translationIndex={`general.fields.${key}`} value={value ? value : '-'} key={idx} />
                    )
                })}</div>
            </div>
        }
    }

    const renderDetailsForFreelancer = (user, skipIndexs = []) => {
        const isBranch = !!user?.company_name;
        const keys = Object.keys(user);

        let orderedKeys = keys;
        if (isBranch && keys.includes('company_name')) {
            orderedKeys = ['company_name', ...keys.filter(k => k !== 'company_name')];
        }

        return orderedKeys.map((item, idx) => {
            if (skipIndexs.includes(item)) {
                return null;
            }

            if (item === 'branches') {
                if (user['branches']?.length === 1) {
                    return parseBranchesField(user, item, idx, 0);
                }
            }

            if (
                [
                    'id',
                    'is_internal_client',
                    'approved_by',
                    'approved_at',
                    'type',
                    'average_rating',
                    'roles',
                    'contractType',
                    'max_hourly_rate_vk',
                    'max_hourly_rate_zk',
                    'confirmed_message',
                    'max_hourly_rate_vk',
                    'max_hourly_rate_zk',
                    'shift_confirmed_message',
                    'alias',
                    'invoice_email',
                    'invoice_company_name',
                    'invoice_vat_number',
                    'invoice_street',
                    'invoice_number',
                    'invoice_bus',
                    'invoice_city',
                    'invoice_postal',
                    'contracts_email',
                    'contracts_firstname',
                    'contracts_lastname',
                    'performancestatement_email',
                    'performancestatement_firstname',
                    'performancestatement_lastname',
                ].includes(item)
            ) return false;
            if (item === 'address') return (parseAddressField(user, item, idx));
            if (item === 'company') {
                return (
                    <TextInputReadOnly translationIndex={`general.fields.${item}`} value={user[item]?.name ?? '-'} key={idx} />
                );
            }
            if (item === 'media') {
                return <div className="grid grid-cols-2 gap-4" key={idx}>
                    {
                        Object.entries(user[item] ?? {})?.map(([key, value], idx) => {
                            if (value === null) {
                                return <div
                                    className="mb-4 flex flex-row items-center justify-start"
                                    key={idx}>
                                    <p className="font-bold leading-3 underline mr-2">{t(`general.fields.${key}`)}: </p>
                                    <span>{t('general.labels.no_file')}</span>
                                </div>
                            }

                            return (
                                <div
                                    className="mb-4 flex flex-row items-center justify-start"
                                    key={idx}>
                                    <p className="font-bold leading-3 underline mr-2">{t(`general.fields.${key}`)}:</p>
                                    <a href={value?.url} target="_blank" className="group"
                                        download={true}>
                                        <FontAwesomeIcon className="group-hover:text-primary" icon={faDownload} />
                                    </a>
                                </div>
                            )
                        })
                    }
                </div>
            }
            return (
                <TextInputReadOnly translationIndex={`general.fields.${item}`} value={user[item]} key={idx} />
            );
        });
    }

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between mb-4'}>
                    <div>
                        <h1 className={'text-2xl font-semibold'}>{t('admin.shift_details.title')}</h1>
                        <div className={"flex flex-row group items-center"} onClick={() => {
                            navigate('/admin/shifts');
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft}
                                className={'text-sm transition-all group-hover:mr-2 duration-500 cursor-pointer'} />

                            <div
                                className={'text-sm text-gray-500 my-auto cursor-pointer ml-2'}>{t('general.actions.back_to_overview')}</div>
                        </div>
                    </div>

                    <div className={'flex flex-col justify-center'}>
                        <DangerButton onClick={() => setOpenDeleteConfirmation(true)}>{t('shifts.delete_shift')}</DangerButton>
                    </div>
                </div>
            </div>
            <div className={'grid grid-cols-1 md:grid-cols-3 md:space-x-4 space-y-4 md:space-y-0'}>
                <div className={'md:col-span-2 space-y-4'}>
                    <BaseCard border={false}>
                        <form className="w-full" onSubmit={(e) => e.preventDefault()}>

                            <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('shifts.shift_info')}</h2>

                            <div className="mb-2">
                                <label htmlFor="freelancer_id" className="block text-left text-sm font-medium leading-6 text-gray-900 mb-2">
                                    {t('shifts_module.sections_freelancer_heading')}
                                </label>
                                <div className="flex gap-2 items-start">
                                    <div className="flex-1">
                                        <SelectInput
                                            name={'freelancer_id'}
                                            options={freelancers
                                                .filter((freelancer) => {
                                                    const possibility = getValues('shift_applications_possibility') ?? 'both';
                                                    if (possibility === 'both') return true;
                                                    const fn = freelancer.function;
                                                    if (possibility === 'only_verpleegkundige') return ['A1', 'A2'].includes(fn);
                                                    if (possibility === 'only_zorgkundige') return ['A0', 'A3'].includes(fn);
                                                    return true;
                                                })
                                                .map((freelancer) => {
                                                    return {
                                                        id: freelancer.id,
                                                        value: freelancer.id,
                                                        label: `${freelancer.first_name || freelancer.firstname} ${freelancer.last_name || freelancer.lastname}`
                                                    }
                                                })}
                                            helperText={t('shifts.freelancer_select_helper')}
                                            control={control}
                                            value={getValues('freelancer_id') ?? null}
                                            setValue={setValue}
                                            placeholder={t('shifts.freelancer')}
                                        />
                                    </div>

                                    {shiftDetail?.freelancer?.id && (
                                        <button
                                            type="button"
                                            onClick={() => setShowWithdrawConfirmation(true)}
                                            className="rounded-md border border-solid border-red-500 cursor-pointer px-3 py-2 text-sm font-medium shadow-sm transition-all duration-300 ease-in-out bg-white text-red-600 hover:bg-red-50 whitespace-nowrap h-[38px] shrink-0"
                                        >
                                            {t('admin.shift_details.withdraw_application')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {matchingStatutenOptions.length > 1 && (
                                <div className="mb-2">
                                    <SelectInput
                                        name={'applied_as_statuut'}
                                        options={matchingStatutenOptions}
                                        control={control}
                                        value={getValues('applied_as_statuut')}
                                        setValue={setValue}
                                        translationIndex={'shifts_module.applied_as_statuut'}
                                    />
                                </div>
                            )}

                            {shiftDetail?.freelancer?.id && shiftDetail?.confirmed_applied_as_statuut && (
                                <div className="mb-2 flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t('shifts_module.confirmed_applied_as_statuut')}:
                                    </span>
                                    <span className="text-xs bg-primary text-white px-2 py-1 rounded">
                                        {t(`statuten.${shiftDetail.confirmed_applied_as_statuut}`, shiftDetail.confirmed_applied_as_statuut)}
                                    </span>
                                </div>
                            )}

                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                                <DateTimePickerInput
                                    label={'Start time'}
                                    name={'start_time'}
                                    setValue={setValue}
                                    control={control}
                                    currentValue={getValues('start_time')}
                                    translationIndex={'general.fields.start_time'}
                                    errors={errors}
                                />
                                <DateTimePickerInput
                                    label={'End time'}
                                    name={'end_time'}
                                    setValue={setValue}
                                    control={control}
                                    currentValue={getValues('start_time')}
                                    translationIndex={'general.fields.end_time'}
                                    errors={errors}
                                />
                            </div>

                            <div className="mb-2">
                                <SelectInput
                                    name={'shift_applications_possibility'}
                                    options={shiftApplicationPossibilities}
                                    setValue={setValue}
                                    control={control}
                                    value={getValues('shift_applications_possibility') ?? null}
                                    placeholder={t('general.fields.shift_applications_possibility')}
                                    translationIndex={'shifts.shift_applications_possibility'}
                                    isDisabled={!!shiftDetail?.freelancer?.id}
                                />
                            </div>

                            <div className="mb-2">
                                <SelectInput
                                    name={'allowed_statuten'}
                                    options={statutenOptions}
                                    multiple={true}
                                    setValue={setValue}
                                    control={control}
                                    currentValue={getValues('allowed_statuten') ?? ['freelance']}
                                    translationIndex={'shifts.allowed_statuten'}
                                    helperText={shiftDetail?.freelancer?.id ? t('shifts.allowed_statuten_locked_helper') : t('shifts.allowed_statuten_helper')}
                                    errors={errors}
                                    isDisabled={!!shiftDetail?.freelancer?.id}
                                />
                            </div>

                            <div className="mb-2">
                                <NumberInput
                                    register={register}
                                    name={'break_duration'}
                                    translationIndex={'general.fields.break_duration'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <TextAreaInput
                                    register={register}
                                    name={'remarks'}
                                    translationIndex={'general.fields.remarks'}
                                    errors={errors}
                                />
                            </div>
                            <div className="mb-2">
                                <div className="flex flex-row justify-start">
                                    <ControlledSwitch
                                        control={control}
                                        name={'is_published'}
                                        getValues={getValues}
                                        setValue={setValue}
                                        errors={errors}
                                        translationIndex={'general.fields.is_published'}
                                    />
                                </div>
                            </div>
                            <InterruptedShiftToggleRow
                                control={control}
                                getValues={getValues}
                                setValue={setValue}
                                errors={errors}
                                showAsManual={
                                    !resetIntent &&
                                    (
                                        (shiftDetail?.interrupted_shift_done_by_admin ?? false) ||
                                        (watchedInterruptedShift ?? false) !== (shiftDetail?.interrupted_shift ?? false)
                                    )
                                }
                                onReset={handleResetInterrupted}
                                t={t}
                            />
                            <PrimaryButton onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
                                {t('shifts.save_shift')}
                            </PrimaryButton>
                        </form>
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('shifts_module.sections_freelancer_heading')}</h2>
                        {
                            !shiftDetail?.freelancer && (
                                <p className={'text-sm text-gray-600'}>{t('shifts.no_freelancer_confirmed')}</p>
                            )
                        }

                        {
                            shiftDetail?.freelancer && (renderDetailsForFreelancer(shiftDetail?.freelancer))
                        }
                    </BaseCard>

                    <BaseCard border={false}>
                        <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('tables.customer')}</h2>
                        {
                            !shiftDetail?.branch && (
                                <p className={'text-sm text-gray-600'}>
                                    {t('admin.shift_details.no_customer_error')}
                                    <ol>
                                        <li>Shift ID: {shiftId}</li>
                                    </ol>
                                </p>
                            )
                        }

                        {
                            shiftDetail?.branch && (renderDetailsForFreelancer(shiftDetail?.branch, ['average_rating', 'hourly_rate']))
                        }
                    </BaseCard>

                    <BaseCard border={false}>
                        <MailHistoryList filters={{ shiftId: shiftId }} />
                    </BaseCard>
                </div>

                <div className={'space-y-4'}>
                    <BaseCard border={false}>
                        <div className={'flex flex-row justify-between align-middle'}>
                            <h2 className={'text-lg text-gray-700 uppercase mb-1 mt-2'}>{t('admin.shift_details.contract')}</h2>
                            {
                                ['completed'].includes(shiftDetail?.contract?.status) && (
                                    <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70 my-auto'}>{t('shifts.status_signed')}</div>
                                )
                            }

                            {
                                ['cancelled'].includes(shiftDetail?.contract?.status) && (
                                    <div
                                        className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70 h-fit my-auto'}>{t('shifts.status_cancelled')}</div>
                                )
                            }

                            {
                                ['sent'].includes(shiftDetail?.contract?.status) && (
                                    <div
                                        className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70 h-fit my-auto'}>{t('shifts.status_sent')}</div>
                                )
                            }

                            {
                                ['pending'].includes(shiftDetail?.contract?.status) && (
                                    <div
                                        className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70 h-fit my-auto'}>{t('shifts.status_created')}</div>
                                )
                            }
                        </div>
                        {
                            !shiftDetail?.contract && (
                                <p className={'text-sm text-gray-600'}>{t('shifts.no_contract_yet')}</p>
                            )
                        }

                        {
                            shiftDetail?.contract && (
                                <div>
                                    <TextInputReadOnly translationIndex={'general.fields.contract_id'} value={shiftDetail?.contract?.id} />
                                    <TextInputReadOnly translationIndex={'general.fields.documenso_id'} value={shiftDetail?.contract?.documenso_id} />

                                    {
                                        shiftDetail?.contract?.document && (
                                            <div className="mb-4 flex flex-row items-center justify-start">
                                                <p className="font-bold leading-3 underline mr-2">Document:</p>
                                                <a href={shiftDetail?.contract?.document?.url} target="_blank" className="group" download={true}>
                                                    <FontAwesomeIcon className="group-hover:text-primary" icon={faDownload} />
                                                </a>
                                            </div>
                                        )
                                    }

                                    {
                                        shiftDetail?.contract?.document === null && (
                                            <div className="mb-4 flex flex-row items-center justify-start">
                                                <p className="font-bold leading-3 underline mr-2">Document:</p>
                                                <span>{t('general.labels.no_file')}</span>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                        }
                    </BaseCard>
                </div>
            </div>

            <DeleteConfirmDialog open={openDeleteConfirmation} onClose={() => setOpenDeleteConfirmation(false)} onConfirm={onDeleteShift} />

            {showWithdrawConfirmation && (
                <Dialog className="relative z-[60]" open={showWithdrawConfirmation} onClose={() => !isWithdrawing && setShowWithdrawConfirmation(false)}>
                    <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <DialogPanel className="bg-white rounded-lg p-6 max-w-md w-full">
                            <DialogTitle className="text-lg font-semibold text-gray-900 mb-4">
                                {t('admin.shift_details.withdraw_application')}
                            </DialogTitle>
                            <p className="text-gray-600 mb-6">
                                {t('admin.shift_details.withdraw_confirmation_message')}
                            </p>
                            <div className="flex gap-4 justify-end">
                                <button
                                    onClick={() => setShowWithdrawConfirmation(false)}
                                    disabled={isWithdrawing}
                                    className="rounded-md border border-solid border-gray-300 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('general.actions.cancel')}
                                </button>
                                <button
                                    onClick={handleWithdrawFreelancer}
                                    disabled={isWithdrawing}
                                    className="rounded-md border border-solid border-red-600 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-red-600 text-white hover:bg-red-700 hover:border-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isWithdrawing ? t('shifts.withdrawing') : t('shifts.confirm_withdraw')}
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>
            )}
        </div>
    )
}

